import { useState } from 'react';
import { Typography, Box, Stack } from '@mui/material';
import { Button } from '@liminal/web-ui/primitives/Button';
import { Modal } from '@liminal/web-ui/overlays/Modal';
import { ConfirmationDialog } from '@liminal/web-ui/overlays/ConfirmationDialog';
import { Textarea } from '@liminal/web-ui/forms/Textarea';
import { Input } from '@liminal/web-ui/forms/Input';
import { FormErrorBanner } from '@liminal/web-ui/forms/FormErrorBanner';
import { useCreateNimAnnouncement } from '@/hooks/useNimAnnouncements';
import { AudienceSelector, type AudienceSelection } from '@/features/announcements/components/AudienceSelector';
import { useOpeningKey } from '@/hooks/useOpeningKey';
import { getErrorMessage } from '@/errors/messages';
import { buttonError } from './firstMessageButton';

const CONTENT_MAX = 4000;
// The backend's bounds on the optional button (chat_service api.proto).
const BUTTON_LABEL_MAX = 40;
const BUTTON_URI_MAX = 2048;

interface NimFirstMessageFormDialogProps {
  open: boolean;
  onClose: () => void;
}

export function NimFirstMessageFormDialog(props: NimFirstMessageFormDialogProps) {
  // The form's state belongs to one opening of the dialog.
  const openingKey = useOpeningKey(props.open);
  return <NimFirstMessageForm key={openingKey} {...props} />;
}

function NimFirstMessageForm({ open, onClose }: NimFirstMessageFormDialogProps) {
  const createMutation = useCreateNimAnnouncement();

  const [content, setContent] = useState('');
  const [buttonLabel, setButtonLabel] = useState('');
  const [buttonUri, setButtonUri] = useState('');
  const [error, setError] = useState('');
  const [confirmSend, setConfirmSend] = useState(false);
  const [audience, setAudience] = useState<AudienceSelection>({ valid: true, summary: 'all users' });
  // One key per dialog open: a retry of the same submission (double-click,
  // flaky network) reuses it, so the backend creates the announcement once.
  const [idempotencyKey] = useState(() => crypto.randomUUID());

  const submit = async () => {
    try {
      await createMutation.mutateAsync({
        content: content.trim(),
        idempotencyKey,
        ...(audience.audience && { audience: audience.audience }),
        ...(buttonLabel.trim() && { buttonLabel: buttonLabel.trim(), buttonUri: buttonUri.trim() }),
      });
      setConfirmSend(false);
      onClose();
    } catch (e) {
      setError(getErrorMessage(e));
    }
  };

  const handleSubmit = () => {
    if (!content.trim()) {
      setError('Message is required');
      return;
    }
    if (!audience.valid) {
      setError(audience.error ?? 'Select an audience');
      return;
    }
    const buttonProblem = buttonError(buttonLabel.trim(), buttonUri.trim());
    if (buttonProblem) {
      setError(buttonProblem);
      return;
    }
    setError('');
    setConfirmSend(true);
  };

  return (
    <>
      <Modal
        open={open}
        title="New First Message"
        onClose={onClose}
        width="md"
        actions={
          <>
            <Button variant="link" onClick={onClose} disabled={createMutation.isPending}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSubmit} loading={createMutation.isPending}>
              Send
            </Button>
          </>
        }
      >
        <Box sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Opens the next new chat each selected user starts, once, in place of
            Nim's greeting. It reads in Nim's voice — write it as Nim speaking.
          </Typography>
          <AudienceSelector
            key={String(open)}
            onChange={setAudience}
            disabled={createMutation.isPending}
          />
          <Textarea
            label="Message"
            value={content}
            onChange={setContent}
            required
            rows={5}
            maxLength={CONTENT_MAX}
            helper={`${content.length}/${CONTENT_MAX}`}
            placeholder="e.g. Quick heads up — you can now invest in US stocks right from this chat. Want a tour?"
          />
          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Input
                label="Button text (optional)"
                value={buttonLabel}
                onChange={setButtonLabel}
                maxLength={BUTTON_LABEL_MAX}
                placeholder="e.g. See what's new"
              />
            </Box>
            <Box sx={{ flex: 2 }}>
              <Input
                label="Button link (optional)"
                type="url"
                inputMode="url"
                value={buttonUri}
                onChange={setButtonUri}
                maxLength={BUTTON_URI_MAX}
                placeholder="https://"
              />
            </Box>
          </Stack>
          <FormErrorBanner error={confirmSend ? undefined : error || undefined} />
        </Box>
      </Modal>

      <ConfirmationDialog
        open={confirmSend}
        title={`Send to ${audience.summary}?`}
        message={
          audience.audience
            ? 'Each user receives this once, as the first Nim message of the next new chat they start. There is no unsend once delivered — cancelling later only stops undelivered messages.'
            : 'Every user — including people who sign up later — receives this once, as the first Nim message of the next new chat they start, until you cancel it. There is no unsend once delivered.'
        }
        confirmLabel="Send"
        destructive
        loading={createMutation.isPending}
        onConfirm={() => void submit()}
        onCancel={() => setConfirmSend(false)}
        data-testid="confirm-send-nim-announcement"
      >
        <FormErrorBanner error={error || undefined} />
      </ConfirmationDialog>
    </>
  );
}
